import React, { createContext, useState, useContext, useEffect } from 'react';

// Create the context
const ResumeContext = createContext();

/**
 * Provider component that wraps the app and makes resume state available to any
 * child component that calls useResumeContext().
 */
export const ResumeProvider = ({ children }) => {
  // Active section state
  const [activeSection, setActiveSection] = useState('intro');
  
  // Track which sections have been viewed
  const [viewedSections, setViewedSections] = useState({});
  
  // Track which sections are currently expanded
  const [expandedSections, setExpandedSections] = useState({});
  
  // Track overall progress through the resume
  const [progress, setProgress] = useState(0);

  // Update progress whenever viewed sections change
  useEffect(() => {
    const sectionIds = ['intro', 'framework', 'career', 'competency', 'portfolio', 'conclusion'];
    const viewedCount = Object.values(viewedSections).filter(Boolean).length;
    const progressPercentage = Math.floor((viewedCount / sectionIds.length) * 100);
    setProgress(progressPercentage);
  }, [viewedSections]);

  // Mark a section as viewed
  const markSectionViewed = (sectionId) => {
    setViewedSections(prev => ({
      ...prev,
      [sectionId]: true
    }));
  };

  // Toggle a section's expanded state
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Value object that will be passed to consumers
  const value = {
    activeSection,
    setActiveSection,
    viewedSections,
    markSectionViewed,
    expandedSections,
    toggleSection,
    progress
  };

  return (
    <ResumeContext.Provider value={value}>
      {children}
    </ResumeContext.Provider>
  );
};

/**
 * Custom hook that provides access to the resume context.
 * Throws an error if used outside of a ResumeProvider.
 */
export const useResumeContext = () => {
  const context = useContext(ResumeContext);
  if (context === undefined) {
    throw new Error('useResumeContext must be used within a ResumeProvider');
  }
  return context;
};
